import { useState, useEffect, useCallback, createContext, useContext, forwardRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { TRANSITION, motionSafe } from '@/shared/constants/motion';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  action?: ToastAction;
}

interface ToastContextValue {
  toast: (type: ToastType, message: string, duration?: number, action?: ToastAction) => void;
}

/** Toasts stack no higher than this (design system). */
const MAX_TOASTS = 3;

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

const iconMap: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

/**
 * C13, decided: toasts are SOFT (status tint, status-coloured border and icon,
 * ink text), not the blueprint's dark-ink bar. The soft ground keeps the
 * status readable at a glance and the ink message at 12:1+; the action is a
 * secondary button, so a toast never adds a second solid primary to the screen.
 */
const styleMap: Record<ToastType, string> = {
  success: 'border-[var(--color-success)] bg-[var(--color-success-soft)] text-[var(--color-success)]',
  error: 'border-[var(--color-error)] bg-[var(--color-error-soft)] text-[var(--color-error)]',
  warning: 'border-[var(--color-warning)] bg-[var(--color-warning-soft)] text-[var(--color-warning)]',
  info: 'border-[var(--color-info)] bg-[var(--color-info-soft)] text-[var(--color-info)]',
};

const ToastItem = forwardRef<HTMLDivElement, { toast: Toast; onDismiss: (id: string) => void }>(
  function ToastItem({ toast: t, onDismiss }, ref) {
    const reduced = useReducedMotion() ?? false;
    const { t: tr } = useTranslation();
    const Icon = iconMap[t.type];

    // The timer stops while the pointer or focus is on the toast, so an
    // action ("Upgrade") cannot vanish under the cursor.
    const [paused, setPaused] = useState(false);
    useEffect(() => {
      if (paused) return;
      const timer = setTimeout(() => onDismiss(t.id), t.duration ?? 4000);
      return () => clearTimeout(timer);
    }, [t.id, t.duration, onDismiss, paused]);

    return (
      <motion.div
        ref={ref}
        layout={!reduced}
        initial={reduced ? false : { opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={reduced ? undefined : { opacity: 0, y: 10, scale: 0.95 }}
        transition={motionSafe(TRANSITION.panel, reduced)}
        className={[
          'flex items-center gap-3 px-4 py-3 rounded-[10px] border shadow-[var(--shadow-md)]',
          // Never wider than the phone it is on (375px less the 16px gutters).
          'w-[min(420px,calc(100vw-32px))] sm:w-auto sm:min-w-[300px] sm:max-w-[420px]',
          styleMap[t.type],
        ].join(' ')}
        // Errors interrupt; success and info wait their turn.
        role={t.type === 'error' ? 'alert' : 'status'}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused(false);
        }}
      >
        <Icon size={18} className="shrink-0" aria-hidden="true" />
        <p className="flex-1 m-0 text-body font-medium text-[var(--color-ink)]" dir="auto">
          {t.message}
        </p>
        {t.action && (
          <button
            type="button"
            onClick={() => {
              t.action?.onClick();
              onDismiss(t.id);
            }}
            className="btn btn-sm btn-secondary shrink-0"
          >
            {t.action.label}
          </button>
        )}
        <button
          type="button"
          onClick={() => onDismiss(t.id)}
          className="shrink-0 -me-2 w-11 h-11 inline-flex items-center justify-center rounded-[10px] hover:bg-[var(--color-surface-3)] cursor-pointer"
          aria-label={tr('toast.dismiss', { defaultValue: 'Dismiss notification' })}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </motion.div>
    );
  },
);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(
    (type: ToastType, message: string, duration?: number, action?: ToastAction): void => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      // At most three stacked: the oldest gives way to the newest.
      setToasts((prev) => [...prev, { id, type, message, duration, action }].slice(-MAX_TOASTS));
    },
    [],
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast: addToast }}>
      {children}
      {/* Above the phone tab bar (64px + safe area) below lg; bottom-right from lg.
          Portalled to <body> above every overlay (modals and sheets sit at
          z-600, the palette at 200): a toast fired from inside a dialog was
          hidden under its backdrop. */}
      {createPortal(
        <div className="fixed bottom-[calc(80px+env(safe-area-inset-bottom,0px))] lg:bottom-[calc(1rem+var(--dock-h,0px))] end-4 z-[1000] flex flex-col gap-2 items-end">
          <AnimatePresence mode="popLayout">
            {toasts.map((t) => (
              <ToastItem key={t.id} toast={t} onDismiss={dismissToast} />
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
